import { Injectable, signal, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * ThemeService — 主題切換服務（Theme Switching Service）
 *
 * 管理按鈕主題的切換邏輯，支援三套主題：
 * - 'default'  : 預設明亮主題
 * - 'scifi'    : 深色企業科幻主題（Sci-Fi Console）
 * - 'cyberpunk': 賽博龐克主題（Cyberpunk）
 *
 * 功能：
 * 1. 透過 signal() 管理當前主題狀態
 * 2. 操作 document.body.classList 切換 CSS class
 * 3. 使用 localStorage 持久化主題選擇
 */

/** 主題類型定義（Theme Type） */
export type ThemeType = 'default' | 'scifi' | 'cyberpunk';

/** 所有可用主題清單（All Available Themes） */
export const THEME_LIST: { id: ThemeType; label: string; icon: string }[] = [
  { id: 'default',   label: '預設',      icon: '☀️' },
  { id: 'scifi',     label: '科幻',      icon: '🛸' },
  { id: 'cyberpunk', label: '賽博龐克',  icon: '⚡' },
];

/** localStorage 儲存鍵名（Storage Key） */
const STORAGE_KEY = 'jk-ui-theme';

/** body 上使用的 CSS class 前綴（CSS Class Prefix） */
const THEME_CLASS_PREFIX = 'theme-';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);

  /** 當前主題（Current Theme Signal） */
  currentTheme = signal<ThemeType>('default');

  constructor() {
    this.loadTheme();
  }

  /**
   * 切換主題（Set Theme）
   * 1. 移除舊的 theme-* class
   * 2. 加上新的 theme-* class（預設主題不加 class）
   * 3. 更新 signal
   * 4. 寫入 localStorage
   */
  setTheme(theme: ThemeType): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const body = document.body;

    // 移除所有現有的 theme-* class
    body.classList.forEach(cls => {
      if (cls.startsWith(THEME_CLASS_PREFIX)) {
        body.classList.remove(cls);
      }
    });

    // 非預設主題才加上 class
    if (theme !== 'default') {
      body.classList.add(`${THEME_CLASS_PREFIX}${theme}`);
    }

    // 更新 signal 與 localStorage
    this.currentTheme.set(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }

  /**
   * 從 localStorage 載入主題（Load Theme from Storage）
   * 在 constructor 中自動呼叫
   */
  private loadTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const saved = localStorage.getItem(STORAGE_KEY) as ThemeType | null;
    const theme: ThemeType = saved && ['default', 'scifi', 'cyberpunk'].includes(saved)
      ? saved
      : 'default';

    this.setTheme(theme);
  }
}
